import { postLog } from "../services/serviceService.ts"
import { LogRequest } from "../models/Log.ts"

function getSecureRandomString(length: number): string {
    const chars =
        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789"
    let result = ""

    // Use Web Crypto API if available, fall back to Math.random()
    if (globalThis.crypto?.getRandomValues) {
        const bytes = new Uint8Array(length)
        crypto.getRandomValues(bytes)
        for (let i = 0; i < length; i++) {
            result += chars[bytes[i] % chars.length]
        }
    } else {
        // Fallback for non-secure contexts
        for (let i = 0; i < length; i++) {
            result += chars[Math.floor(Math.random() * chars.length)]
        }
    }
    return result
}

// Utility function to detect browser name and version
function getBrowserInfo(): { browser?: string; browser_version?: string } {
    const userAgent = navigator.userAgent
    let browser: string | undefined
    let browser_version: string | undefined

    if (userAgent.includes("Chrome") && !userAgent.includes("Edg")) {
        browser = "Chrome"
        const match = userAgent.match(/Chrome\/(\d+\.\d+)/)
        browser_version = match ? match[1] : undefined
    } else if (userAgent.includes("Firefox")) {
        browser = "Firefox"
        const match = userAgent.match(/Firefox\/(\d+\.\d+)/)
        browser_version = match ? match[1] : undefined
    } else if (userAgent.includes("Safari") && !userAgent.includes("Chrome")) {
        browser = "Safari"
        const match = userAgent.match(/Version\/(\d+\.\d+)/)
        browser_version = match ? match[1] : undefined
    } else if (userAgent.includes("Edg")) {
        browser = "Edge"
        const match = userAgent.match(/Edg\/(\d+\.\d+)/)
        browser_version = match ? match[1] : undefined
    }

    return { browser, browser_version }
}

// Utility function to detect operating system
function getOperatingSystem(): string | undefined {
    const userAgent = navigator.userAgent

    if (userAgent.includes("Windows")) return "Windows"
    if (userAgent.includes("Mac OS")) return "macOS"
    if (userAgent.includes("Linux")) return "Linux"
    if (userAgent.includes("Android")) return "Android"
    if (userAgent.includes("iOS")) return "iOS"
    if (userAgent.includes("iPad")) return "iPadOS"
    if (userAgent.includes("iPhone")) return "iOS"

    return undefined
}

// Utility function to get screen and viewport information
function getScreenInfo(): {
    screen_resolution?: string
    viewport_size?: string
} {
    const screen_resolution =
        screen.width && screen.height
            ? `${screen.width}x${screen.height}`
            : undefined

    const viewport_size =
        window.innerWidth && window.innerHeight
            ? `${window.innerWidth}x${window.innerHeight}`
            : undefined

    return { screen_resolution, viewport_size }
}

// Utility function to generate a session ID (persisted in sessionStorage)
export function getSessionId(): string {
    try {
        let sessionId = sessionStorage.getItem("ddo_session_id")
        if (!sessionId) {
            sessionId = `session_${Date.now()}_${getSecureRandomString(7)}`
            sessionStorage.setItem("ddo_session_id", sessionId)
        }
        return sessionId
    } catch (error) {
        // sessionStorage unavailable, generate temporary ID
        return `session_${Date.now()}_${getSecureRandomString(7)}`
    }
}

// Utility function to generate a user ID (persisted in localStorage)
export function getUserId(): string {
    try {
        let userId = localStorage.getItem("ddo_user_id")
        if (!userId) {
            userId = `user_${Date.now()}_${getSecureRandomString(7)}`
            localStorage.setItem("ddo_user_id", userId)
        }
        return userId
    } catch (error) {
        // localStorage unavailable, generate temporary ID
        return `user_${Date.now()}_${getSecureRandomString(7)}`
    }
}

export function getOriginatingUserId(): string | null {
    try {
        return localStorage.getItem("originating_user_id")
    } catch (error) {
        // localStorage unavailable
        return null
    }
}

// Utility function to get the commit hash from environment variables
export function getCommitHash(): string | undefined {
    return process.env.REACT_APP_COMMIT_SHA || undefined
}

// Utility function to get the commit hash from environment variables
export function getBuildTime(): string | undefined {
    const buildTime = process.env.REACT_APP_BUILD_TIMESTAMP
    return buildTime ? new Date(buildTime).toLocaleString() : undefined
}

// Utility function to get route information
function getRouteInfo(): { route?: string; page_title?: string } {
    const route = window.location.pathname
    const page_title = document.title || undefined

    return { route, page_title }
}

// Utility function to log messages
export default function logMessage(
    message: string,
    level: "debug" | "info" | "warn" | "error" | "fatal",
    options?: Partial<LogRequest>
): void {
    const { browser, browser_version } = getBrowserInfo()
    const os = getOperatingSystem()
    const { screen_resolution, viewport_size } = getScreenInfo()
    const session_id = getSessionId()
    const { route, page_title } = getRouteInfo()
    const user_id = getUserId()
    const originating_user_id = getOriginatingUserId()
    const commit_hash = getCommitHash() || "local_dev"

    const logEntry: LogRequest = {
        message,
        level,
        timestamp: new Date().toISOString(),
        session_id,
        user_id,
        originating_user_id,
        user_agent: navigator.userAgent,
        browser,
        browser_version,
        os,
        screen_resolution,
        viewport_size,
        url: window.location.href,
        page_title,
        referrer: document.referrer || undefined,
        route,
        commit_hash,
        ...options,
    }

    postLog(logEntry).catch((error) => {
        // Fail silently to avoid infinite loops or cascading errors
        // Only log to console if not in production to aid debugging
        if (process.env.NODE_ENV !== "production") {
            console.error("Failed to log message:", error)
        }
    })
}
